import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const QuickImportShortcut = () => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getIngestToken()
      .then((r) => { if (alive) setToken(r.token || ""); })
      .catch(() => { if (alive) toast.error("Couldn't load your import link"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const url = token ? `${API_BASE}/api/ingest/${token}/tour-sheet` : "";

  const copyUrl = () => {
    if (!url) return;
    navigator.clipboard?.writeText(url).catch(() => {});
    toast.success("Import link copied — paste it into the Shortcut's URL field");
  };

  const rotate = async () => {
    setRotating(true);
    try {
      const r = await api.rotateIngestToken();
      setToken(r.token || "");
      toast.success("New import link created — update your Shortcut with the new link");
    } catch {
      toast.error("Couldn't reset the link");
    } finally {
      setRotating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-400" data-testid="shortcut-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your import link…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quick-import-shortcut">
      {/* Personal link */}
      <div>
        <p className="mb-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500">Your private import link</p>
        <div className="flex items-center gap-2">
          <code
            className="min-w-0 flex-1 truncate rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-300"
            data-testid="shortcut-url"
            title={url}
          >
            {url}
          </code>
          <Button onClick={copyUrl} variant="outline" size="sm" className="shrink-0 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid="shortcut-copy-btn">
            <Copy className="mr-1.5 h-4 w-4" /> Copy
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500">Keep this private — anyone with it can add tours to your account.</p>
      </div>

      {/* Setup steps */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-300" data-testid="shortcut-steps">
        <p className="mb-2 flex items-center gap-2 font-600 text-zinc-100"><Wand2 className="h-4 w-4 text-[#E0575B]" /> One-time iPhone setup (~2 min)</p>
        <ol className="ml-4 list-decimal space-y-2 text-zinc-400">
          <li>Open the <span className="font-600 text-zinc-200">Shortcuts</span> app → tap <span className="font-600 text-zinc-200">＋</span> to create a new shortcut. Name it <span className="font-600 text-zinc-200">“Send to Stage Door”</span>.</li>
          <li>Tap the <span className="font-600 text-zinc-200">ⓘ</span> (or settings) and turn on <span className="font-600 text-zinc-200">Show in Share Sheet</span>. Under “Accept”, choose <span className="font-600 text-zinc-200">Images</span>.</li>
          <li>Add the action <span className="font-600 text-zinc-200">Get Contents of URL</span>. Paste your link above into the URL field (tap <span className="font-600 text-zinc-200">Copy</span> first).</li>
          <li>Tap <span className="font-600 text-zinc-200">Show More</span> on that action: set <span className="font-600 text-zinc-200">Method → POST</span>, then <span className="font-600 text-zinc-200">Request Body → Form</span>. Add a field, set its type to <span className="font-600 text-zinc-200">File</span>, name it <span className="font-600 text-zinc-200">file</span>, and set its value to <span className="font-600 text-zinc-200">Shortcut Input</span>.</li>
          <li><span className="text-zinc-500">(Optional)</span> Skip adding a "Show Notification" action — Stage Door will send you its own in-app alert when the tour lands (toggle it under <span className="font-600 text-zinc-200">Settings → Push Notifications → Import alerts</span>).</li>
          <li>Done! Now screenshot → tap <span className="font-600 text-zinc-200">Share</span> → <span className="font-600 text-zinc-200">Send to Stage Door</span>. The tour is created automatically — no saving or opening the app needed.</li>
          <li><span className="font-600 text-emerald-300">Multiple at once:</span> in Photos, tap <span className="font-600 text-zinc-200">Select</span>, pick several screenshots (tour sheets and/or schedules), then Share → Send to Stage Door. They all import together with one summary alert.</li>
        </ol>
        <p className="mt-3 text-xs text-zinc-500">The first time you run it, iOS asks permission to send the image — tap <span className="font-600 text-zinc-300">Allow</span>. Schedules work too: share a schedule screenshot and it imports your shifts.</p>
      </div>

      {/* Reset link */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">Lost your phone or shared the link by mistake?</span>
        <Button onClick={rotate} disabled={rotating} variant="ghost" size="sm" className="text-zinc-400 hover:text-zinc-100" data-testid="shortcut-rotate-btn">
          {rotating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Reset link
        </Button>
      </div>
    </div>
  );
};
